import {
  useState,
  useEffect,
  useReducer,
  useCallback,
  useContext,
} from 'react';
import toastError from '../../toasts/toastError';
import { toast } from 'react-toastify';

import {
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  InputAdornment,
  useTheme,
} from '@mui/material';

import { DeleteOutline, Search, Edit } from '@mui/icons-material';

import MainContainer from '../../components/MainContainer';
import MainHeader from '../../components/MainHeader';
import MainHeaderButtonsWrapper from '../../components/MainHeaderButtonsWrapper';
import Title from '../../components/Title';
import TableRowSkeleton from '../../components/TableRowSkeleton';
import FileModal from '../../components/FileModal';
import ConfirmationModal from '../../components/ConfirmationModal';
import ForbiddenPage from '../../components/ForbiddenPage';

import api from '../../services/api';
import { i18n } from '../../translate/i18n';
import { AuthContext } from '../../context/Auth/AuthContext';
import { getStyled } from './styles';

const reducer = (state, action) => {
  if (action.type === 'LOAD_FILES') {
    const files = action.payload;
    const newFiles = [];

    files.forEach((fileList) => {
      const fileListIndex = state.findIndex((s) => s.id === fileList.id);
      if (fileListIndex !== -1) {
        state[fileListIndex] = fileList;
      } else {
        newFiles.push(fileList);
      }
    });

    return [...state, ...newFiles];
  }

  if (action.type === 'UPDATE_FILES') {
    const fileList = action.payload;
    const fileListIndex = state.findIndex((s) => s.id === fileList.id);

    if (fileListIndex !== -1) {
      state[fileListIndex] = fileList;
      return [...state];
    } else {
      return [fileList, ...state];
    }
  }

  if (action.type === 'DELETE_FILE') {
    const fileListId = action.payload;

    const fileListIndex = state.findIndex((s) => s.id === fileListId);
    if (fileListIndex !== -1) {
      state.splice(fileListIndex, 1);
    }
    return [...state];
  }

  if (action.type === 'RESET') {
    return [];
  }
};

const FileLists = () => {
  const theme = useTheme();
  const classes = getStyled(theme);

  const { user, socket } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedFileList, setSelectedFileList] = useState(null);
  const [deletingFileList, setDeletingFileList] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState('');
  const [files, dispatch] = useReducer(reducer, []);
  const [fileListModalOpen, setFileListModalOpen] = useState(false);

  const fetchFileLists = useCallback(async () => {
    try {
      const { data } = await api.get('/files/', {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: 'LOAD_FILES', payload: data.files });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  }, [searchParam, pageNumber]);

  useEffect(() => {
    dispatch({ type: 'RESET' });
  }, [searchParam]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchFileLists();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, fetchFileLists]);

  useEffect(() => {
    const onFileEvent = (data) => {
      if (data.action === 'update' || data.action === 'create') {
        dispatch({ type: 'UPDATE_FILES', payload: data.files });
      }

      if (data.action === 'delete') {
        dispatch({ type: 'DELETE_FILE', payload: +data.fileId });
      }
    };

    socket.on(`company-${user.companyId}-file`, onFileEvent);
    return () => {
      socket.off(`company-${user.companyId}-file`, onFileEvent);
    };
  }, [socket, user]);

  const handleOpenFileListModal = () => {
    setSelectedFileList(null);
    setFileListModalOpen(true);
  };

  const handleCloseFileListModal = () => {
    setSelectedFileList(null);
    setFileListModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditFileList = (fileList) => {
    setSelectedFileList(fileList);
    setFileListModalOpen(true);
  };

  const handleDeleteFileList = async (fileListId) => {
    try {
      await api.delete(`/files/${fileListId}`);
      toastSuccess(i18n.t('files.toasts.deleted'));
    } catch (err) {
      toastError(err);
    }
    setDeletingFileList(null);
    setSearchParam('');
    setPageNumber(1);

    dispatch({ type: 'RESET' });
    setPageNumber(1);
    await fetchFileLists();
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingFileList && `${i18n.t('files.confirmationModal.deleteTitle')}`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteFileList(deletingFileList.id)}
      >
        {i18n.t('files.confirmationModal.deleteMessage')}
      </ConfirmationModal>
      <FileModal
        open={fileListModalOpen}
        onClose={handleCloseFileListModal}
        reload={fetchFileLists}
        aria-labelledby='form-dialog-title'
        fileListId={selectedFileList && selectedFileList.id}
      />
      {user.profile === 'user' ? (
        <ForbiddenPage />
      ) : (
        <>
          <MainHeader>
            <Title>
              {i18n.t('files.title')} ({files.length})
            </Title>
            <MainHeaderButtonsWrapper>
              <TextField
                placeholder={i18n.t('contacts.searchPlaceholder')}
                type='search'
                variant='standard'
                value={searchParam}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <Search style={{ color: 'gray' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ ...classes.searchField, marginBottom: theme.spacing(1) }}
              />
              <Button
                variant='outlined'
                sx={{
                  ...theme.custom.editButton,
                  marginLeft: theme.spacing(1),
                  marginTop: theme.spacing(1),
                  marginBottom: theme.spacing(1),
                }}
                onClick={handleOpenFileListModal}
              >
                {i18n.t('files.buttons.add')}
              </Button>
            </MainHeaderButtonsWrapper>
          </MainHeader>
          <Paper
            sx={classes.mainPaper}
            variant='outlined'
            onScroll={handleScroll}
          >
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell align='center'>
                    {i18n.t('files.table.name')}
                  </TableCell>
                  <TableCell align='center'>
                    {i18n.t('files.table.actions')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <>
                  {files.map((fileList) => (
                    <TableRow key={fileList.id}>
                      <TableCell align='center'>{fileList.name}</TableCell>
                      <TableCell align='center'>
                        <IconButton
                          size='small'
                          onClick={() => handleEditFileList(fileList)}
                        >
                          <Edit />
                        </IconButton>

                        <IconButton
                          size='small'
                          onClick={() => {
                            setConfirmModalOpen(true);
                            setDeletingFileList(fileList);
                          }}
                        >
                          <DeleteOutline
                            sx={{ color: theme.custom.colors.cancelButton }}
                          />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {loading && <TableRowSkeleton columns={4} />}
                </>
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </MainContainer>
  );
};

export default FileLists;
